import type { SupportLanguage } from "../appModel";

const CATALOG_URL = `${import.meta.env.BASE_URL}house-promotions.json`;
const CATALOG_TIMEOUT_MS = 2_000;
const IMPRESSION_STORAGE_KEY = "wordinarius_echo.web.house_promotions.v1";
const LOCAL_IMAGE_ROOTS = Array.from(new Set([
  `${import.meta.env.BASE_URL}house-promotions/`,
  "/app/house-promotions/",
]));

interface HousePromotionCopy {
  cta: string;
  description: string;
  headline: string;
  destinationUrl?: string;
  imageAlt?: string;
}

interface HousePromotionDefinition {
  destinationUrl: string;
  enabled: boolean;
  endAt: string | null;
  id: string;
  imageSrc: string;
  mobileImageSrc: string | null;
  imageAlt: string;
  locales: Partial<Record<SupportLanguage, HousePromotionCopy>>;
  productName: string;
  priority: number;
  startAt: string | null;
}

export interface HousePromotionCatalog {
  enabled: boolean;
  promotions: HousePromotionDefinition[];
  version: 1;
}

export interface HousePromotionView {
  cta: string;
  description: string;
  destinationUrl: string;
  headline: string;
  id: string;
  imageSrc: string;
  mobileImageSrc: string | null;
  imageAlt: string;
  language: SupportLanguage;
  productName: string;
}

interface SeenState {
  day: string;
  ids: string[];
}

let catalogPromise: Promise<HousePromotionCatalog | null> | null = null;
let sessionSeenState: SeenState | null = null;

export function __resetHousePromotionCatalogForTests(): void {
  catalogPromise = null;
  sessionSeenState = null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function boundedString(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized && normalized.length <= maxLength ? normalized : null;
}

function parseHttpsUrl(value: unknown): string | null {
  const candidate = boundedString(value, 500);
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === "https:" && !parsed.username && !parsed.password ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function parseLocalImage(value: unknown): string | null {
  const candidate = boundedString(value, 240);
  if (!candidate) return null;
  const root = LOCAL_IMAGE_ROOTS.find((localRoot) => candidate.startsWith(localRoot));
  if (!root) return null;
  const relativePath = candidate.slice(root.length);
  if (!/^[a-z0-9][a-z0-9/_-]*\.(?:avif|png|webp)$/i.test(relativePath)) return null;
  return candidate;
}

function parseCopy(value: unknown, destinationUrl: string): HousePromotionCopy | null {
  if (!isRecord(value)) return null;
  const cta = boundedString(value.cta, 24);
  const description = boundedString(value.description, 140);
  const headline = boundedString(value.headline, 52);
  const localizedDestination = value.destinationUrl === undefined ? undefined : parseHttpsUrl(value.destinationUrl);
  const imageAlt = value.imageAlt === undefined ? undefined : boundedString(value.imageAlt, 240);
  if (localizedDestination === null || imageAlt === null) return null;
  if (localizedDestination && new URL(localizedDestination).origin !== new URL(destinationUrl).origin) return null;
  return cta && description && headline ? { cta, description, headline, destinationUrl: localizedDestination, imageAlt } : null;
}

function parseOptionalDate(value: unknown): string | null | undefined {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) return undefined;
  return value;
}

function parsePromotion(value: unknown): HousePromotionDefinition | null {
  if (!isRecord(value)) return null;
  const destinationUrl = parseHttpsUrl(value.destinationUrl);
  const endAt = parseOptionalDate(value.endAt);
  const id = boundedString(value.id, 48);
  const imageSrc = parseLocalImage(value.imageSrc);
  const mobileImageSrc = value.mobileImageSrc === undefined ? null : parseLocalImage(value.mobileImageSrc);
  const imageAlt = value.imageAlt === undefined ? "" : boundedString(value.imageAlt, 240);
  const productName = boundedString(value.productName, 40);
  const startAt = parseOptionalDate(value.startAt);
  const priority = typeof value.priority === "number"
    && Number.isInteger(value.priority)
    && value.priority >= 1
    && value.priority <= 10
    ? value.priority
    : null;
  if (!destinationUrl || endAt === undefined || !id || !imageSrc || !productName || !priority || startAt === undefined || imageAlt === null || (value.mobileImageSrc !== undefined && !mobileImageSrc)) {
    return null;
  }
  if (!/^[a-z0-9][a-z0-9_-]*$/.test(id)) return null;

  const rawLocales = isRecord(value.locales) ? value.locales : {};
  const locales: Partial<Record<SupportLanguage, HousePromotionCopy>> = {};
  for (const language of ["en", "tr", "ar"] as const) {
    const copy = parseCopy(rawLocales[language], destinationUrl);
    if (copy) locales[language] = copy;
  }
  if (Object.keys(locales).length === 0) return null;

  return {
    destinationUrl,
    enabled: value.enabled !== false,
    endAt,
    id,
    imageSrc,
    mobileImageSrc,
    imageAlt,
    locales,
    productName,
    priority,
    startAt,
  };
}

export function parseHousePromotionCatalog(value: unknown): HousePromotionCatalog | null {
  if (!isRecord(value) || value.version !== 1 || !Array.isArray(value.promotions)) return null;
  const promotions = value.promotions.map(parsePromotion).filter((promotion): promotion is HousePromotionDefinition => Boolean(promotion));
  if (new Set(promotions.map((promotion) => promotion.id)).size !== promotions.length) return null;
  return {
    enabled: value.enabled === true,
    promotions,
    version: 1,
  };
}

function dayKey(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function readSeenState(storage: Storage | null, now: Date): SeenState {
  const day = dayKey(now);
  const sessionIds = sessionSeenState?.day === day ? sessionSeenState.ids : [];
  if (!storage) return { day, ids: [...sessionIds] };
  try {
    const parsed = JSON.parse(storage.getItem(IMPRESSION_STORAGE_KEY) || "null") as Partial<SeenState> | null;
    if (parsed?.day !== dayKey(now) || !Array.isArray(parsed.ids)) {
      return { day, ids: [...sessionIds] };
    }
    return {
      day: parsed.day,
      ids: Array.from(new Set([...sessionIds, ...parsed.ids.filter((id): id is string => typeof id === "string")])),
    };
  } catch {
    return { day, ids: [...sessionIds] };
  }
}

function hash(value: string): number {
  let result = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }
  return result >>> 0;
}

export function selectHousePromotion(
  catalog: HousePromotionCatalog | null,
  options: {
    language: SupportLanguage;
    now?: Date;
    seenIds?: string[];
  },
): HousePromotionView | null {
  if (!catalog?.enabled) return null;
  const now = options.now || new Date();
  const timestamp = now.getTime();
  const seen = new Set(options.seenIds || []);
  const candidates = catalog.promotions.filter((promotion) => {
    if (!promotion.enabled || seen.has(promotion.id)) return false;
    if (promotion.startAt && Date.parse(promotion.startAt) > timestamp) return false;
    if (promotion.endAt && Date.parse(promotion.endAt) < timestamp) return false;
    return Boolean(promotion.locales[options.language] || promotion.locales.en);
  });
  if (candidates.length === 0) return null;

  const weighted = candidates.flatMap((promotion) => Array.from(
    { length: promotion.priority },
    () => promotion,
  ));
  const promotion = weighted[hash(`${dayKey(now)}:${options.language}`) % weighted.length];
  const language = promotion.locales[options.language] ? options.language : "en";
  const copy = promotion.locales[language];
  if (!copy) return null;
  return {
    ...copy,
    destinationUrl: copy.destinationUrl || promotion.destinationUrl,
    id: promotion.id,
    imageSrc: promotion.imageSrc,
    mobileImageSrc: promotion.mobileImageSrc,
    imageAlt: copy.imageAlt || promotion.imageAlt,
    language,
    productName: promotion.productName,
  };
}

async function fetchCatalog(): Promise<HousePromotionCatalog | null> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), CATALOG_TIMEOUT_MS);
  try {
    const response = await fetch(CATALOG_URL, {
      cache: "no-cache",
      credentials: "same-origin",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    return parseHousePromotionCatalog(await response.json());
  } catch {
    return null;
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

export function preloadHousePromotionCatalog(): Promise<HousePromotionCatalog | null> {
  catalogPromise ||= fetchCatalog();
  return catalogPromise;
}

function browserStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

export async function getHousePromotion(
  language: SupportLanguage,
  now = new Date(),
): Promise<HousePromotionView | null> {
  const catalog = await preloadHousePromotionCatalog();
  if (!catalog?.enabled) return null;
  const seenState = readSeenState(browserStorage(), now);
  return selectHousePromotion(catalog, {
    language,
    now,
    seenIds: seenState.ids,
  });
}

export function markHousePromotionShown(id: string, now = new Date()): void {
  if (typeof window === "undefined") return;
  const state = readSeenState(browserStorage(), now);
  if (!state.ids.includes(id)) state.ids.push(id);
  sessionSeenState = state;
  try {
    browserStorage()?.setItem(IMPRESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // The promotion remains optional when managed browsers block storage.
  }
}
